import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Property, SearchFilters } from "../types/search";
import { useSearchQuery } from "../hooks/search/useSearchQuery";
import { useDirectSearchQuery } from "../hooks/search/useDirectSearchQuery";
import { useTenant } from "../contexts/TenantContext";
import { LandingSearchBox } from "../components/features/search/LandingSearchBox";
import { resolveSearchCity } from "../utils/searchTransitionUtils";
import SearchResults from "../components/features/search/SearchResults";
import MapView from "../components/features/search/MapView";
import type { ViewMode } from "../components/features/search/ViewToggle";
import ViewToggle from "../components/features/search/ViewToggle";
import { useMLSLogos } from "../hooks/mls/useMLSLogos";

export const SearchPage = () => {
  const { config } = useTenant();
  // Set default view mode to "combined"
  const [viewMode, setViewMode] = useState<ViewMode>("combined");
  const [geocodedResults, setGeocodedResults] = useState<Property[]>([]);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const navigate = useNavigate();

  // Initialize both search hooks
  const regularSearch = useSearchQuery();
  const directSearch = useDirectSearchQuery();

  // Track current search query to determine which hook to use
  const [currentQuery, setCurrentQuery] = useState("");

  // Determine if we should use direct search (no query) or regular search
  const isDirectSearch = !currentQuery;

  // Log which search hook is being used
  console.log(
    `Using ${
      isDirectSearch ? "DIRECT SEARCH" : "REGULAR SEARCH"
    } hook. Query: "${currentQuery}"`
  );

  // Use the appropriate search hook based on whether we have a query or not
  const {
    results,
    loading,
    error,
    hasMore,
    filters,
    search,
    loadMore,
    totalAvailable = 0,
    retryFailedRequest,
    timestamp,
  } = isDirectSearch ? directSearch : regularSearch;

  // Log the current state for debugging
  console.log("Current state:", {
    isDirectSearch,
    results: results.length,
    loading,
    error,
    currentQuery,
  });

  // Fetch MLS logos for all properties
  const { mlsLogosMap, mlsComplianceQueries } = useMLSLogos(results);

  useEffect(() => {
    // Map API coordinates to the format expected by MapView
    const propertiesWithCoordinates = results.map((property) => {
      const mapped = {
        ...property,
        address: {
          ...property.address,
          coordinates:
            property.property?.latitude && property.property?.longitude
              ? {
                  lat: parseFloat(property.property.latitude),
                  lng: parseFloat(property.property.longitude),
                }
              : undefined,
        },
      };

      return mapped;
    });

    setGeocodedResults(propertiesWithCoordinates);
  }, [results]);

  const [displayQuery, setDisplayQuery] = useState("");
  const defaultLocation = `${config?.orgData?.client_address?.city || ""}, ${
    config?.orgData?.client_address?.state || ""
  }`.trim();
  const initialSearchTriggered = useRef(false);

  // Store the current map center coordinates
  const [mapCenter, setMapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const handleSearch = useCallback(
    (newFilters: SearchFilters) => {
      // Update current query for determining which search hook to use
      const searchQuery = newFilters.query || "";
      setCurrentQuery(searchQuery);

      // Determine if this is a direct search (no query) or regular search
      const isNewDirectSearch = !searchQuery;

      // Log which search function is being used for this search
      console.log(
        `Search performed using ${
          isNewDirectSearch ? "DIRECT SEARCH" : "REGULAR SEARCH"
        } function. Query: "${searchQuery}"`
      );

      // Use the appropriate search function based on whether a query is provided
      const searchFn = isNewDirectSearch
        ? directSearch.search
        : regularSearch.search;

      // If we have map coordinates from drag, use them
      if (mapCenter) {
        searchFn({
          ...newFilters,
          ...(searchQuery ? { query: searchQuery } : {}),
          tenant_id: config?.id || "",
          location: {
            city: resolveSearchCity(
              newFilters,
              config?.orgData?.client_address?.city || ""
            ),
            state: config?.orgData?.client_address?.state || "",
            latitude: mapCenter.lat,
            longitude: mapCenter.lng,
          },
        });
      } else {
        // Otherwise use client address
        searchFn({
          ...newFilters,
          ...(searchQuery ? { query: searchQuery } : {}),
          tenant_id: config?.id || "",
          location: {
            city: resolveSearchCity(
              newFilters,
              config?.orgData?.client_address?.city || ""
            ),
            state: config?.orgData?.client_address?.state || "",
          },
        });
      }
    },
    [
      regularSearch.search,
      directSearch.search,
      config?.orgData?.client_address,
      mapCenter,
      config?.id,
    ]
  );

  // Debounce function for map drag events
  const debounceTimeout = useRef<number | null>(null);

  // Handle map drag end event with debounce
  const handleMapDragEnd = useCallback(
    (center: { lat: number; lng: number }) => {
      setMapCenter(center);

      // Clear any existing timeout
      if (debounceTimeout.current) {
        window.clearTimeout(debounceTimeout.current);
      }

      // Set a new timeout to trigger search after delay
      debounceTimeout.current = window.setTimeout(() => {
        // Use the appropriate search function based on whether a query is provided
        const searchFn = isDirectSearch
          ? directSearch.search
          : regularSearch.search;

        // Trigger a new search with the updated coordinates
        searchFn({
          ...filters,
          tenant_id: config?.id || "",
          location: {
            city: resolveSearchCity(
              filters,
              config?.orgData?.client_address?.city || ""
            ),
            state: config?.orgData?.client_address?.state || "",
            latitude: center.lat,
            longitude: center.lng,
          },
        });
        debounceTimeout.current = null;
      }, 300); // 300ms debounce
    },
    [
      isDirectSearch,
      regularSearch.search,
      directSearch.search,
      filters,
      config?.id,
      config?.orgData?.client_address,
    ]
  );

  const handlePropertyClick = useCallback(
    (property: Property) => {
      // Open property in new tab with additional query parameters
      const tenantId = config?.id || "";
      const mlsOsn = property.mlsBoardCode || "";
      window.open(
        `/property/${property.id}?tenant_id=${tenantId}&mls_osn=${mlsOsn}`,
        "_blank"
      );
    },
    [navigate, config?.id]
  );

  /*>>>> TEMP ORIG: window.open(`/property/${property.id}`, "_blank");
  const handlePropertyClick = useCallback(
    (property: Property) => {
      // Open property in new tab using listingKey
      window.open(`/property/${property.id}`, "_blank");
    },
    [navigate]
  );
  */

  // Trigger initial search only once
  useEffect(() => {
    if (
      !initialSearchTriggered.current &&
      config?.id && // Make sure config is loaded
      defaultLocation &&
      handleSearch &&
      results.length === 0
    ) {
      initialSearchTriggered.current = true;

      handleSearch({
        query: "",
        tenant_id: config.id,
        location: {
          city: config?.orgData?.client_address?.city || "",
          state: config?.orgData?.client_address?.state || "",
        },
        filters: {
          standard_status: ["Active", "Active Under Contract"],
          property_sub_type: "Single Family Residence",
        },
      });
    }
  }, [config, defaultLocation, handleSearch, results.length]);

  return (
    <div className="flex flex-col h-full  max-w-[1600px] 3xl:max-w-[1800px] mx-auto px-4 xl:px-6">
      <main className="flex-1 flex flex-col">
        {/* Search Header */}
        <div
          className="py-3 sticky top-[5rem] lg:top-[6rem] z-20 bg-white border-b-2"
          style={{
            borderColor: `${config?.branding?.colors?.text || "#000000"}11`,
          }}
        >
          <div className="flex flex-col lg:flex-row items-end lg:items-center lg:justify-between py-2 gap-2">
            <div className="w-full">
              <LandingSearchBox
                onSearch={handleSearch}
                initialFilters={{ ...filters, query: displayQuery }}
                isLoading={loading}
                onQueryChange={setDisplayQuery}
                showFilters={true}
                filterBorderColor={config?.branding?.colors?.primary}
                filterTextColor={config?.branding?.colors?.primary}
              />
            </div>
            <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 relative overflow-hidden mt-6">
          {viewMode === "properties" && (
            <div className="w-full bg-white">
              <SearchResults
                results={results}
                loading={loading}
                error={error}
                hasMore={hasMore}
                onLoadMore={loadMore}
                onPropertyClick={handlePropertyClick}
                totalAvailable={totalAvailable}
                onRetry={retryFailedRequest}
                filters={filters?.filters}
                searchQuery={filters?.query}
                timestamp={timestamp}
                mlsLogosMap={mlsLogosMap}
                mlsComplianceQueries={mlsComplianceQueries}
              />
            </div>
          )}

          {viewMode === "map" && (
            <div className="w-full h-[600px]">
              <MapView
                properties={geocodedResults}
                onPropertySelect={handlePropertyClick}
                defaultLocation={defaultLocation}
                onMapDragEnd={handleMapDragEnd}
                isLoading={loading}
                mlsLogosMap={mlsLogosMap}
              />
            </div>
          )}

          {viewMode === "combined" && (
            <div className="flex flex-col md:flex-row gap-8 relative">
              <div
                className="w-full md:w-1/2 md:overflow-y-auto md:max-h-[calc(100vh-200px)] md:pr-4 md:scrollbar-thin md:scrollbar-thumb-gray-300 md:scrollbar-track-gray-100 md:hover:scrollbar-thumb-gray-400"
                style={{
                  borderColor: config?.branding?.colors?.text
                    ? `${config.branding.colors.text}11`
                    : "#00000011",
                }}
              >
                <SearchResults
                  results={results}
                  loading={loading}
                  error={error}
                  hasMore={hasMore}
                  onLoadMore={loadMore}
                  onPropertyClick={handlePropertyClick}
                  totalAvailable={totalAvailable}
                  onRetry={retryFailedRequest}
                  gridColumns={2}
                  filters={filters?.filters}
                  searchQuery={filters?.query}
                  timestamp={timestamp}
                  mlsLogosMap={mlsLogosMap}
                  mlsComplianceQueries={mlsComplianceQueries}
                />
              </div>
              <div className="w-full md:w-1/2 h-[calc(100vh-200px)] md:sticky md:top-[200px] bg-white rounded-lg shadow-sm">
                <MapView
                  properties={geocodedResults}
                  onPropertySelect={handlePropertyClick}
                  defaultLocation={defaultLocation}
                  onMapDragEnd={handleMapDragEnd}
                  isLoading={loading}
                  mlsLogosMap={mlsLogosMap}
                />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SearchPage;
